import { MarkerPin01 } from "@untitledui/icons";
import type { MouseEvent } from "react";
import { useCallback, useState } from "react";
import { useAddressResolver } from "../../../hooks";
import type { RoutePointCardProps } from "../../../types/cards.types";
import { formatTime } from "../../../utils/tracking.utils";
import { AddressDisplay } from "../address-display";
import { BaseCard } from "../base-card";

export const RoutePointCard = ({ data, style }: RoutePointCardProps) => {
	const isStopped = data.speed <= 0;
	const {
		address,
		isLoading: isFetchingAddress,
		fetchIfMissing,
	} = useAddressResolver({
		latitude: data.latitude,
		longitude: data.longitude,
	});
	const [showAddress, setShowAddress] = useState(false);
	const latitude = data.latitude?.replace(",", ".").trim();
	const longitude = data.longitude?.replace(",", ".").trim();
	const hasCoordinates = Boolean(latitude && longitude);

	const openMaps = () => {
		if (!hasCoordinates) {
			return;
		}

		window.open(
			`https://www.google.com/maps?q=${latitude},${longitude}`,
			"_blank",
			"noopener,noreferrer"
		);
	};

	const handleOpenMaps = (e: MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		openMaps();
	};

	const handleAddressToggle = useCallback(() => {
		const nextValue = !showAddress;
		setShowAddress(nextValue);
		if (nextValue) {
			fetchIfMissing();
		}
	}, [fetchIfMissing, showAddress]);

	return (
		<BaseCard className="w-full" style={style} variant="vehicle">
			<div className="flex flex-col gap-2">
				<div
					aria-label={isStopped ? "Veículo parado" : "Veículo em movimento"}
					className="flex items-center gap-2 rounded-md border border-[#D5D7DA] px-2 py-0.5"
					role="status"
				>
					<div
						className={`h-2 w-2 rounded-full ${
							isStopped
								? "bg-[#F04438] shadow-[#F04438]/50 shadow-lg"
								: "animate-pulse bg-[#17B26A] shadow-[#17B26A]/50 shadow-lg"
						}`}
					/>
					<span className="font-normal text-[#414651] text-sm">
						{isStopped ? "Parado" : "Movendo"}
					</span>
				</div>

				<AddressDisplay
					address={address}
					className="min-w-0 flex-1"
					isLoading={isFetchingAddress}
					onToggle={handleAddressToggle}
					showToggle={showAddress}
					variant="toggle"
				/>

				<div className="flex items-center gap-2.5">
					<MarkerPin01 className="h-4 w-4 text-gray-500" size={16} />
					{hasCoordinates ? (
						<button
							aria-label="Abrir localização no Google Maps"
							className="min-w-0 flex-1 truncate text-left text-[14px] text-gray-500 underline"
							onClick={handleOpenMaps}
							title="Abrir localização no Google Maps"
							type="button"
						>
							Abrir Maps
						</button>
					) : (
						<span className="text-[14px] text-gray-400">
							Abrir Maps indisponível
						</span>
					)}
				</div>

				<div className="border-slate-100">
					<p className="text-slate-400 text-xs">
						Horário da posição: {formatTime(data.dateTelemetry)}
					</p>
					<p className="text-slate-400 text-xs">
						Velocidade: {data.speed} km/h
					</p>
				</div>
			</div>
		</BaseCard>
	);
};
